import { CustomerTypeEnum, UserContext } from '../gen/models'
import { isCloakedUser } from './cloak'

/**
 * #7806 — whether the Supplier Portal is open to this principal. Rollout is decided by the
 * LaunchDarkly `supplier-portal` flag for SUPPLIERS; a CLOAKED supplier-typed principal (an admin
 * cloaked into a supplier) is not a supplier being rolled out — in Appian the cloak IS the
 * authorization — so the flag does not gate them.
 *
 * ⚠️ The `isSupplier` conjunct is load-bearing and must not be dropped: without it a HOSPITAL-cloaked
 * admin would be admitted to the Supplier Portal, a grant nobody asked for. Locked by
 * SupplierPortalGuardedRoute.test.tsx R2.
 *
 * This is the portal-access predicate of record — any new gate on this surface uses it rather than
 * reading the flag again (the #7701 lesson: one predicate, not three).
 *
 * ⚠️ #7357 (PRD R3, AC 7) added the `!isUncloakedAdmin` conjunct: a NON-cloaked Curvo admin whose own
 * customer is supplier-typed (the QA-account shape) has no portal. A CLOAKED admin reports
 * `isAdmin: false` — cloaking clears the flag server-side (`auth.service.ts:312`/`:319`) — so the term
 * never touches the cloaked population this predicate exists to admit; a real supplier is never an admin;
 * and every non-supplier principal already fails `isSupplier` before the term is read. Locked by
 * SupplierPortalGuardedRoute.test.tsx R3 / R3-control.
 */
export const hasSupplierPortalAccess = (me: UserContext | undefined, supplierPortalEnabled: unknown): boolean => {
  const isSupplier = me?.user.customer.type === CustomerTypeEnum.Supplier
  const isUncloakedAdmin = me?.isAdmin === true
  return isSupplier && !isUncloakedAdmin && (!!supplierPortalEnabled || isCloakedUser(me?.user))
}
